import React, { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import { Select } from 'antd'
import axios from 'axios'
import { useSelectedWeek } from '../../../../../../contexts/SelectedWeekContext'

const { Option } = Select
interface DataItem {
  week: string
  nCasesNowcast: number | null
  lowerCiBaselineNCases: number | null
  upperCiBaselineNCases: number | null
}
interface Week {
  label: string
  value: string
}
interface Trace {
  x: string[]
  y: (number | null)[]
  type: string
  mode: string
  marker?: { color: string }
  line?: { color: string }
  yaxis?: string
  name: string
}
const StreamgraphChart2: React.FC = () => {
  const [data, setData] = useState<DataItem[]>([])
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
  const [diagnosisCategories, setDiagnosisCategories] = useState([])
  const [selectedDiagnosisCategories, setSelectedDiagnosisCategories] =
    useState<string>('all_resp_diags')
  const [ageClass, setAgeClass] = useState([])
  const [selectedAgeClass, setSelectedAgeClass] = useState<string[]>(['0-200'])
  const [loading, setLoading] = useState(true)
  const [plotData, setPlotData] = useState<Trace[]>([])
  const API_URL = process.env.REACT_APP_API_URL
  const [showArrow, setShowArrow] = useState<boolean>(false)

  function parseAgeClasses(ageClasses: string[]): string {
    const parsedString = ageClasses.map((age) => `ageClass%5B%5D=${age}`).join('&')
    return parsedString
  }

  function addMissingWeek(data: DataItem[]): DataItem[] {
    const startWeek = '2025-W35' // First week in the data
    const endWeek = '2026-W27' // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: DataItem[] = []
    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          nCasesNowcast: null,
          lowerCiBaselineNCases: null,
          upperCiBaselineNCases: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week <= '2025-W52') {
        insertionIndex = i + 1
      } else {
        break
      }
    }

    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }

  const fetchData = async (
    ageClasses = selectedAgeClass,
    diagnosis = selectedDiagnosisCategories
  ) => {
    try {
      let traces: Trace[] = []

      // Fetch data for each age class and generate traces
      await Promise.all(
        ageClasses.map(async (ageClass, index) => {
          // Pass index to get color from colors array
          let filterAge = parseAgeClasses([ageClass])

          let apiUrl = `/hospitalPrivateAdmissionsRespiratorySyndromes/list?${filterAge}&diagnosisCategory=${diagnosis}`

          const response = await axios.get(API_URL + apiUrl)

          let readableAge
          let selectedColor
          switch (ageClass) {
            case '[1 - 5[ year(s)':
              readableAge = '1 to 5 years'
              selectedColor = '#FF2F00'
              break
            case '[5 - 19[ year(s)':
              readableAge = '5 to 19 years'
              selectedColor = '#33FF57'
              break
            case '[20 - 49[ year(s)':
              readableAge = '20 to 49 years'
              selectedColor = '#5733FF'
              break
            case '[50 - 64[ year(s)':
              readableAge = '50 to 64 years'
              selectedColor = '#FF33DD'
              break
            case '65 years and older':
              readableAge = '65 years and older'
              selectedColor = '#33DDFF'
              break
            default:
              readableAge = 'All ages'
              selectedColor = '#203879'
              break
          }
          const trace: Trace = {
            x: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
              })
              .map((entry) => entry.week),
            y: addMissingWeek(response.data)
              .filter((entry) => {
                const currentYear = parseInt(entry.week.substring(0, 4), 10)
                const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
                return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
              })
              .map((entry) => entry.nCasesNowcast),
            yaxis: 'y',
            type: 'scatter',
            mode: 'lines',
            line: { color: selectedColor }, // Assign color based on index
            name: `${readableAge} Cases`, // Add age class to trace name
          }
          traces.push(trace)
        })
      )

      const filterAge = parseAgeClasses(['0-200'])
      let apiUrl = `/hospitalPrivateAdmissionsRespiratorySyndromes/list?${filterAge}&diagnosisCategory=${diagnosis}`

      if (diagnosis === '') {
        apiUrl = `/hospitalPrivateAdmissionsRespiratorySyndromes/list?${filterAge}`
      }
      const response = await axios.get(API_URL + apiUrl)

      let data = response.data.filter((entry) => {
        const currentYear = parseInt(entry.week.substring(0, 4), 10)
        const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
        return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
      })
      data = addMissingWeek(data)
      const trace1 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
          })
          .map((entry) => entry.lowerCiBaselineNCases),

        type: 'scatter',
        mode: 'lines',
        //yaxis: 'y2',
        line: { color: 'lightgrey', dash: 'dashdot' },
        name: 'min all ages',
        showlegend: false,
      }

      const trace2 = {
        x: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
          })
          .map((entry) => entry.week),
        y: addMissingWeek(response.data)
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
          })
          .map((entry) => entry.upperCiBaselineNCases),
        fill: 'tonexty',
        type: 'scatter',
        //yaxis: 'y2',
        mode: 'lines',
        line: { color: '#BBBBBC', dash: 'dashdot' },
        name: 'Statistical range of normal percentage*',
      }
      if (ageClasses.includes('0-200') && ageClasses.length <= 1) {
        traces.unshift(trace2)
        traces.unshift(trace1)
      }
      // let endWeek = response.data[response.data.length - 1].week.split('-W')
      let endWeek = '2026-W27'.split('-W')
      const selectWeek = selectedWeek.split('-W')

      const showArrow =
        (Number(selectWeek[0]) > 2025 ||
          (Number(selectWeek[0]) === 2025 && Number(selectWeek[1]) >= 36)) &&
        (Number(selectWeek[0]) < Number(endWeek[0]) ||
          (Number(selectWeek[0]) === Number(endWeek[0]) &&
            Number(selectWeek[1]) <= Number(endWeek[1])))
      setShowArrow(showArrow)

      const startWeek = '2025-W35' // First week in the data
      const endWeek2 = '2026-W27' // Last week in the data
      const existingWeeks = new Set(response.data.map((item) => item.week)) // Set to store existing weeks
      const newData: DataItem[] = []
      const missingData: DataItem[] = []

      let currentWeek = startWeek

      while (currentWeek !== endWeek2) {
        // Move to the next week
        const [year, weekNum] = currentWeek.split('-W')
        const weekNumber = parseInt(weekNum)
        if (weekNumber < 52) {
          currentWeek = `${year}-W${(weekNumber + 1).toString()}`
        } else {
          currentWeek = `${parseInt(year) + 1}-W1`
        }
        if (!existingWeeks.has(currentWeek)) {
          missingData.push({
            week: currentWeek,
            nCasesNowcast: null,
            lowerCiBaselineNCases: null,
            upperCiBaselineNCases: null,
          })
        }
      }

      const trace3: Trace = {
        x: missingData
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
          })
          .map((entry) => entry.week),
        y: missingData
          .filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
          })
          .map((entry) => 0),
        yaxis: 'y',
        type: 'scatter',
        mode: 'markers',
        marker: { color: '#000000' }, // Assign color based on index
        name: `Missing Data `, // Add age class to trace name
      }
      traces.push(trace3)
      setPlotData(traces)
    } catch (error) {
      console.error('Error fetching data:', error)
      // Handle error or set a default value for data
      setData([])
    }
  }

  useEffect(() => {
    const listDiagnosisCategory = async () => {
      try {
        const response = await axios.get(
          API_URL + '/hospitalPrivateAdmissionsRespiratorySyndromes/listDiagnosisCategory'
        )

        const data = response.data.filter((category) => category !== 'GENERAL_RI')
        setDiagnosisCategories(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }

    const listAgeClass = async () => {
      try {
        const response = await axios.get(
          API_URL + '/hospitalPrivateAdmissionsRespiratorySyndromes/listAgeClass'
        )
        const data = response.data
        setAgeClass(data)
      } catch (error) {
        console.error('Error fetching data:', error)
        return null
      }
    }

    listDiagnosisCategory()
    listAgeClass()
  }, [])
  useEffect(() => {
    fetchData()
  }, [selectedAgeClass, selectedDiagnosisCategories, selectedWeek])

  const layout = {
    title: 'Number of hospital admissions of patients with a diagnosis of respiratory syndromes',
    barmode: 'stack',
    bargap: 0.2, // Adjust the gap between bars
    bargroupgap: 0.1, // Adjust the gap between bar groups
    autosize: true,

    annotations: [
      {
        xref: 'paper',
        yref: 'paper',
        x: -0.035,
        xanchor: 'right',
        y: 0.5,
        yanchor: 'bottom',
        text: '#',
        font: {
          size: 25,

          color: '#203879',
        },
        showarrow: false,
      },
      {
        x: selectedWeek,
        y: 0,
        xref: 'x',
        yref: 'y',
        text: showArrow ? 'Selected Week' : '',
        font: {
          family: 'Courier New, monospace',
          size: 16,
          color: '#CC1B1B',
        },
        arrowcolor: '#CC1B1B',
        showarrow: showArrow,
        arrowhead: 7,
        ax: 0,
        ay: -200,
      },
    ],

    xaxis: {
      title: 'Weeks',
      autorange: true,
      type: 'category',
      tickangle: -45,
    },
    yaxis: {
      autorange: true,
    },
    yaxis2: {
      title: {
        text: 'Statistical range of normal percentage*',
        font: {
          size: 25,
          color: '#BBBBBC',
        },
      },
      showgrid: false,
      // titlefont: {color: 'rgb(148, 103, 189)'},
      // tickfont: {color: 'rgb(148, 103, 189)'},
      overlaying: 'y',
      side: 'right',
    },
    legend: {
      x: 0,
      y: -0.4,
      orientation: 'h',
    },
  }

  function handleDataChangeAge(value): void {
    if (value.length === 0) {
      setSelectedAgeClass(['0-200'])
    } else {
      setSelectedAgeClass(value)
    }
  }

  const handleDataChangeVirus = (value) => {
    setSelectedDiagnosisCategories(value)
  }

  return (
    <>
      <div className='row'>
        <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
          Age group :
        </label>
        <div className='col-6'>
          <Select
            variant='borderless'
            className='border border-gray-500 border-3 rounded'
            style={{ width: '71%' }}
            size='large'
            value={selectedAgeClass}
            onChange={(value) => handleDataChangeAge(value)}
            mode='multiple'
          >
            <Option key={'0-200'} value={'0-200'}>
              {'All ages'}
            </Option>
            <Option key={'[1 - 5[ year(s)'} value={'[1 - 5[ year(s)'}>
              {'1 to 5 years'}
            </Option>
            <Option key={'[5 - 19[ year(s)'} value={'[5 - 19[ year(s)'}>
              {'5 to 19 years'}
            </Option>
            <Option key={'[20 - 49[ year(s)'} value={'[20 - 49[ year(s)'}>
              {'20 to 49 years'}
            </Option>
            <Option key={'[50 - 64[ year(s)'} value={'[50 - 64[ year(s)'}>
              {'50 to 64 years'}
            </Option>
            <Option key={'65 years and older'} value={'65 years and older'}>
              {'65 years and older'}
            </Option>
          </Select>
        </div>
        <div className='row mt-2'>
          <label style={{ width: '10%' }} className='col-1 mt-3 text-primary fw-medium fs-5'>
            Virus :
          </label>
          <div className='col-6'>
            <Select
              variant='borderless'
              className='border border-gray-500 border-3 rounded'
              style={{ width: '72.5%' }}
              size='large'
              value={selectedDiagnosisCategories}
              onChange={(value, option) => handleDataChangeVirus(value)}
            >
              <Option key={'all_resp_diags'} value={'all_resp_diags'}>
                {'All virus'}
              </Option>
              <Option key={'FLU'} value={'FLU'}>
                {'Influenza'}
              </Option>
              <Option key={'COVID-19'} value={'COVID-19'}>
                {'SARS-CoV-2'}
              </Option>
              <Option key={'RSV'} value={'RSV'}>
                {'RSV'}
              </Option>
              <Option key={'RI_OTHER_VIRUS'} value={'RI_OTHER_VIRUS'}>
                {'Other respiratory infections'}
              </Option>
            </Select>
          </div>
        </div>
      </div>

      <Plot
        style={{ width: '100%', height: '400px' }}
        data={plotData}
        layout={layout}
        config={{
          displaylogo: false,
          responsive: true,
        }}
      />
    </>
  )
}

export default StreamgraphChart2
